/**
 * evalLabels.js — Degerlendirme rozeti render-aninda i18n
 *
 * EVAL_LABELS, backend/i18n_labels.py icindeki EVALUATION_LABELS'in BIREBIR mirror'idir
 * (6 dil, tum key'ler, emoji dahil). Worker analiz-aninda bu string'lerden birini yazar
 * (analiz diline gore). UI dili degisince render-aninda cevirmek icin reverse-map kullanilir.
 *
 * Kaynak: backend/i18n_labels.py:10-78 (SALT-OKUNUR — degisirse burayi da guncelle).
 */

// backend/i18n_labels.py EVALUATION_LABELS — BIREBIR kopya
export const EVAL_LABELS = {
  en: {
    very_high: "Very High 🟢",
    high: "High 🟢",
    average: "Average 🟡",
    low: "Low 🔴",
    very_low: "Very Low 🔴",
    inactive: "Inactive 🔴",
    na: "N/A",
    insufficient_data: "Insufficient Data",
    hidden_likes: "Cannot calculate - Likes hidden",
    too_fresh: "Cannot be calculated, data too fresh",
  },
  tr: {
    very_high: "Çok Yüksek 🟢",
    high: "Yüksek 🟢",
    average: "Ortalama 🟡",
    low: "Düşük 🔴",
    very_low: "Çok Düşük 🔴",
    inactive: "İnaktif 🔴",
    na: "Hesaplanamadı",
    insufficient_data: "Yetersiz Veri",
    hidden_likes: "Hesaplanamadı - Like'lar gizli",
    too_fresh: "Hesaplanamadı, Veriler Çok Taze",
  },
  ar: {
    very_high: "عالي جداً 🟢",
    high: "عالي 🟢",
    average: "متوسط 🟡",
    low: "ضعيف 🔴",
    very_low: "ضعيف جداً 🔴",
    inactive: "غير نشط 🔴",
    na: "غير متاح",
    insufficient_data: "بيانات غير كافية",
    hidden_likes: "لا يمكن حسابها - الإعجابات مخفية",
    too_fresh: "تعذّر الحساب، البيانات حديثة جداً",
  },
  fr: {
    very_high: "Très élevé 🟢",
    high: "Élevé 🟢",
    average: "Moyen 🟡",
    low: "Faible 🔴",
    very_low: "Très faible 🔴",
    inactive: "Inactif 🔴",
    na: "N/A",
    insufficient_data: "Données insuffisantes",
    hidden_likes: "Impossible à calculer - Likes masqués",
    too_fresh: "Impossible à calculer, données trop récentes",
  },
  es: {
    very_high: "Muy alto 🟢",
    high: "Alto 🟢",
    average: "Promedio 🟡",
    low: "Bajo 🔴",
    very_low: "Muy bajo 🔴",
    inactive: "Inactivo 🔴",
    na: "N/D",
    insufficient_data: "Datos insuficientes",
    hidden_likes: "No se puede calcular - Likes ocultos",
    too_fresh: "No se puede calcular, datos demasiado recientes",
  },
  de: {
    very_high: "Sehr hoch 🟢",
    high: "Hoch 🟢",
    average: "Durchschnitt 🟡",
    low: "Niedrig 🔴",
    very_low: "Sehr niedrig 🔴",
    inactive: "Inaktiv 🔴",
    na: "N/V",
    insufficient_data: "Unzureichende Daten",
    hidden_likes: "Kann nicht berechnet werden - Likes versteckt",
    too_fresh: "Nicht berechenbar, Daten zu frisch",
  },
};

// emoji strip + trim + lowercase + ardisik bosluklari teke indir
const normalize = (s) =>
  (s || "")
    .replace(/[🟢🟡🟠🔴]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

// EVAL_LABELS'in TUM dillerindeki TUM string'leri -> key. "Yuksek 🟢" / "High 🟢" / "Hoch 🟢" hepsi -> "high".
const EVAL_REVERSE = (() => {
  const map = {};
  for (const lang of Object.keys(EVAL_LABELS)) {
    for (const key of Object.keys(EVAL_LABELS[lang])) {
      map[normalize(EVAL_LABELS[lang][key])] = key;
    }
  }
  return map;
})();

/**
 * Worker'in yazdigi ham degerlendirme string'ini aktif UI diline cevirir (emoji'li doner).
 * Bilinmeyen / bos string'de ham degeri oldugu gibi dondurur (regresyon korumasi).
 */
export const translateEval = (storedValue, locale) => {
  if (!storedValue || storedValue === "-" || storedValue === "N/A") return storedValue;
  const key = EVAL_REVERSE[normalize(storedValue)];
  if (!key) return storedValue;
  return EVAL_LABELS[locale]?.[key] ?? EVAL_LABELS.tr[key] ?? storedValue;
};

/**
 * Ham degerlendirme string'ini (dil-bagimsiz) key'e cevirir (renk/severity icin).
 * Bilinmeyen / '-' / bos -> null.
 */
export const evalKeyOf = (val) => EVAL_REVERSE[normalize(val)] || null;

// v30.71i: Degerlendirme enum'larini PERFORMANS sirasina gore sayisal rank'e cevir (sort icin).
const EVAL_RANK = { very_high: 5, high: 4, average: 3, low: 2, very_low: 1, inactive: 0 };

/**
 * Ham degerlendirme string'ini (dil-bagimsiz) sirali sayiya cevirir.
 * Bos/'-'/'N/A'/bilinmeyen -> -1 (en sona).
 */
export const evalSortRank = (storedValue) => {
  if (!storedValue || storedValue === "-" || storedValue === "N/A") return -1;
  const key = EVAL_REVERSE[normalize(storedValue)];
  return key && EVAL_RANK[key] !== undefined ? EVAL_RANK[key] : -1;
};
